import React, { useState, useMemo, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useProdutos } from '../../hooks/useProdutos';
import { useTiposPagamento } from '../../hooks/useTiposPagamento';
import {
  useRegistrarPedido,
  useListarMesasAbertas,
  useAdicionarItemPedido,
  useRemoverItemPedido,
  useFecharMesa,
  usePagamentoParcial,
  useAtualizarDescontoPedido,
  useCancelarPedido,
  useAlterarFuncionarioPedido,
} from '../../hooks/usePedidos';
import { usePedidosDoDia, useStatusPedidos, useAlterarStatusPedido } from '../../hooks/useStatusPedidos';
import { useObterEstabelecimento, useListarFuncionarios } from '../../hooks/useAdmin';
import { useAuth } from '../../contexts/AuthContext';
import { usePageTitle } from '../../hooks/usePageTitle';
import Button from '../../components/Common/Button';
import ConfirmModal from '../../components/Common/ConfirmModal';
import { MesaAbertaDto, ItemPedido, TipoUsuario } from '../../types';

interface ItemCarrinho {
  id: string;
  produtoId: number;
  produtoNome: string;
  precoUnitario: number;
  quantidade: number;
  sabor?: string;
}

const PDV: React.FC = () => {
  usePageTitle('PDV');
  const { user } = useAuth();
  const { data: produtos, isLoading: loadingProdutos } = useProdutos(true);
  const { data: tiposPagamento, isLoading: loadingTipos } = useTiposPagamento();
  const { mutate: registrarPedido, isPending } = useRegistrarPedido();
  const { data: statusPedidos, isLoading: loadingStatus } = useStatusPedidos();
  const { mutate: alterarStatus, isPending: alterandoStatus } = useAlterarStatusPedido();
  const { data: estabelecimento } = useObterEstabelecimento(user?.estabelecimentoId);
  const { data: mesasAbertas, isLoading: loadingMesas } = useListarMesasAbertas();
  const { mutate: adicionarItem, isPending: adicionandoItem } = useAdicionarItemPedido();
  const { mutate: removerItem } = useRemoverItemPedido();
  const { mutate: fecharMesa, isPending: fechandoMesa } = useFecharMesa();
  const { mutate: pagamentoParcial, isPending: pagandoParcial } = usePagamentoParcial();
  const { mutate: atualizarDesconto, isPending: atualizandoDesconto } = useAtualizarDescontoPedido();
  const { mutate: cancelarPedido, isPending: cancelandoPedido } = useCancelarPedido();
  const { mutate: alterarFuncionario, isPending: alterandoFuncionario } = useAlterarFuncionarioPedido();

  const isGerente = user?.tipo === TipoUsuario.GerenteEstabelecimento;
  const { data: funcionarios } = useListarFuncionarios(
    isGerente ? { estabelecimentoId: user?.estabelecimentoId } : undefined
  );

  const quantidadeMesas = estabelecimento?.quantidadeMesas ?? 0;

  const [buscaProduto, setBuscaProduto] = useState('');
  const [carrinho, setCarrinho] = useState<ItemCarrinho[]>([]);
  const [funcionarioCarrinhoId, setFuncionarioCarrinhoId] = useState<number | null>(null);
  const [tipoPagamentoId, setTipoPagamentoId] = useState<number | null>(null);
  const [desconto, setDesconto] = useState('');
  const [pago, setPago] = useState(false);
  const [numeroMesa, setNumeroMesa] = useState<number | ''>('');
  const [vendaRetroativa, setVendaRetroativa] = useState(false);
  const [dataHoraRetroativa, setDataHoraRetroativa] = useState('');
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [pedidoExpandido, setPedidoExpandido] = useState<number | null>(null);
  const tamanhoPagina = 10;

  // Modal seleção de sabor
  const [modalSabor, setModalSabor] = useState<{
    isOpen: boolean;
    produto: { id: number; nome: string; preco: number; sabores: string[] } | null;
  }>({ isOpen: false, produto: null });

  // Modal adicionar itens a mesa aberta
  const [modalAdicionarItens, setModalAdicionarItens] = useState<{
    mesaId: number | null;
    aberto: boolean;
    quantidades: Record<number, number>;
    sabores: Record<number, string>;
  }>({ mesaId: null, aberto: false, quantidades: {}, sabores: {} });

  // Modal fechar mesa
  const [modalFecharMesa, setModalFecharMesa] = useState<{
    mesaId: number | null;
    tipoPagamentoId: number | null;
    modo: 'unica' | 'dividir';
    itensSelecionados: Record<number, number>;
  }>({ mesaId: null, tipoPagamentoId: null, modo: 'unica', itensSelecionados: {} });

  // Modal editar desconto
  const [modalEditarDesconto, setModalEditarDesconto] = useState<{
    pedidoId: number | null;
    descontoAtual: string;
  }>({ pedidoId: null, descontoAtual: '' });

  // Modal cancelar pedido
  const [modalCancelarPedido, setModalCancelarPedido] = useState<{
    isOpen: boolean;
    pedidoId: number | null;
    numeroMesa: number | null;
  }>({
    isOpen: false,
    pedidoId: null,
    numeroMesa: null,
  });

  // Modal confirmar remoção de item
  const [modalRemoverItem, setModalRemoverItem] = useState<{
    isOpen: boolean;
    pedidoId: number | null;
    itemId: number | null;
    itemNome: string;
  }>({
    isOpen: false,
    pedidoId: null,
    itemId: null,
    itemNome: '',
  });

  // Modal confirmar alteração de status
  const [confirmarModal, setConfirmarModal] = useState<{
    isOpen: boolean;
    pedidoId: number | null;
    novoStatusId: number | null;
    statusNome: string;
  }>({
    isOpen: false,
    pedidoId: null,
    novoStatusId: null,
    statusNome: '',
  });

  const [produtoAnimando, setProdutoAnimando] = useState<number | null>(null);

  const [editandoFuncionario, setEditandoFuncionario] = useState<{
    pedidoId: number;
    usuarioId: number | null;
  } | null>(null);

  const { data: dadosPedidos, isLoading: loadingPedidos } = usePedidosDoDia(paginaAtual, tamanhoPagina);

  const produtosPorCategoria = useMemo(() => {
    if (!produtos) return [];
    let produtosDisponiveis = produtos.filter(p => !p.sobEncomenda);

    if (buscaProduto.trim()) {
      const termo = buscaProduto.toLowerCase();
      produtosDisponiveis = produtosDisponiveis.filter(p =>
        p.nome.toLowerCase().includes(termo) ||
        p.descricao?.toLowerCase().includes(termo) ||
        p.categoriaProdutoNome?.toLowerCase().includes(termo)
      );
    }

    const grupos: { categoria: string; produtos: typeof produtosDisponiveis }[] = [];
    const mapa = new Map<string, typeof produtosDisponiveis>();

    for (const p of produtosDisponiveis) {
      const cat = p.categoriaProdutoNome || 'Sem categoria';
      if (!mapa.has(cat)) mapa.set(cat, []);
      mapa.get(cat)!.push(p);
    }

    const categoriasOrdenadas = Array.from(mapa.keys()).sort((a, b) => {
      if (a === 'Sem categoria') return 1;
      if (b === 'Sem categoria') return -1;
      return a.localeCompare(b, 'pt-BR');
    });

    for (const cat of categoriasOrdenadas) {
      const prods = mapa.get(cat)!.sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
      grupos.push({ categoria: cat, produtos: prods });
    }

    return grupos;
  }, [produtos, buscaProduto]);

  const adicionarAoCarrinho = useCallback((produtoId: number, nome: string, preco: number, sabor?: string) => {
    setProdutoAnimando(produtoId);
    setTimeout(() => setProdutoAnimando(null), 400);

    const itemId = `${produtoId}__${sabor || ''}`;
    setCarrinho((prev) => {
      const itemExistente = prev.find((item) => item.id === itemId);
      if (itemExistente) {
        return prev.map((item) =>
          item.id === itemId ? { ...item, quantidade: item.quantidade + 1 } : item
        );
      }
      return [...prev, { id: itemId, produtoId, produtoNome: nome, precoUnitario: preco, quantidade: 1, sabor }];
    });
  }, []);

  const handleClickProduto = useCallback((produto: { id: number; nome: string; precoUnitario: number; sabores?: string[] }) => {
    if (produto.sabores && produto.sabores.length > 0) {
      setModalSabor({ isOpen: true, produto: { id: produto.id, nome: produto.nome, preco: produto.precoUnitario, sabores: produto.sabores } });
    } else {
      adicionarAoCarrinho(produto.id, produto.nome, produto.precoUnitario);
    }
  }, [adicionarAoCarrinho]);

  const removerDoCarrinho = (itemId: string) => {
    setCarrinho((prev) => prev.filter((item) => item.id !== itemId));
  };

  const atualizarQuantidade = (itemId: string, quantidade: number) => {
    if (quantidade <= 0) {
      removerDoCarrinho(itemId);
      return;
    }
    setCarrinho((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, quantidade } : item))
    );
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getStatusBadgeClass = (statusDescricao: string) => {
    const status = statusDescricao.toLowerCase();

    if (status.includes('aguardando') || status.includes('pendente')) {
      return 'bg-warning-50 text-warning-700 border border-warning-200';
    }
    if (status.includes('pago') || status.includes('concluido') || status.includes('conclu')) {
      return 'bg-success-50 text-success-700 border border-success-200';
    }
    if (status.includes('cancelado')) {
      return 'bg-danger-50 text-danger-700 border border-danger-200';
    }
    if (status.includes('estornado')) {
      return 'bg-neutral-100 text-neutral-700 border border-neutral-200';
    }
    return 'bg-primary-50 text-primary-700 border border-primary-200';
  };

  const agruparItens = (itens: ItemPedido[]) => {
    const mapa = new Map<string, ItemPedido & { valorTotal: number }>();
    for (const item of itens) {
      const chave = `${item.produtoId}__${item.sabor || ''}`;
      if (mapa.has(chave)) {
        const g = mapa.get(chave)!;
        g.quantidade += item.quantidade;
        g.valorTotal += item.valorTotal;
      } else {
        mapa.set(chave, { ...item });
      }
    }
    return Array.from(mapa.values()).sort((a, b) => a.nomeProduto.localeCompare(b.nomeProduto, 'pt-BR'));
  };

  const subtotal = carrinho.reduce((sum, item) => sum + item.precoUnitario * item.quantidade, 0);
  const descontoNum = parseFloat(desconto.replace(',', '.')) || 0;
  const total = subtotal - descontoNum;

  // Mesa derivada da query para dados sempre frescos
  const mesaParaFechar = mesasAbertas?.find(m => m.id === modalFecharMesa.mesaId) ?? null;

  // Subtotal dos itens selecionados no modo dividir
  const subtotalSelecionados = useMemo(() => {
    if (!mesaParaFechar || modalFecharMesa.modo !== 'dividir') return 0;
    return mesaParaFechar.itens.reduce((sum, item) => {
      const qtdSelecionada = modalFecharMesa.itensSelecionados[item.id] || 0;
      return sum + item.precoUnitario * qtdSelecionada;
    }, 0);
  }, [mesaParaFechar, modalFecharMesa.itensSelecionados, modalFecharMesa.modo]);

  const temItensSelecionados = Object.values(modalFecharMesa.itensSelecionados).some(q => q > 0);

  const finalizarVenda = () => {
    const exigePagamento = pago || quantidadeMesas < 1;

    if (exigePagamento && !tipoPagamentoId) {
      toast.error('Selecione um tipo de pagamento', {
        duration: 3000,
        position: 'top-center',
      });
      return;
    }

    if (carrinho.length === 0) {
      toast.error('Adicione produtos ao carrinho', {
        duration: 3000,
        position: 'top-center',
      });
      return;
    }

    if (vendaRetroativa && !dataHoraRetroativa) {
      toast.error('Informe a data e hora da venda retroativa', {
        duration: 3000,
        position: 'top-center',
      });
      return;
    }

    registrarPedido(
      {
        tipoPagamentoId: exigePagamento ? tipoPagamentoId! : undefined,
        desconto: descontoNum,
        pago: exigePagamento,
        numeroMesa: numeroMesa !== '' ? numeroMesa : undefined,
        usuarioId: funcionarioCarrinhoId ?? undefined,
        itens: carrinho.map((item) => ({
          produtoId: item.produtoId,
          quantidade: item.quantidade,
          sabor: item.sabor,
        })),
        ...(vendaRetroativa && dataHoraRetroativa
          ? { dataHora: dataHoraRetroativa }
          : {}),
      },
      {
        onSuccess: () => {
          setCarrinho([]);
          setDesconto('');
          setTipoPagamentoId(null);
          setPago(false);
          setNumeroMesa('');
          setVendaRetroativa(false);
          setDataHoraRetroativa('');
          setFuncionarioCarrinhoId(null);
        },
      }
    );
  };

  const handleAlterarStatus = (pedidoId: number, novoStatusId: number) => {
    const statusSelecionado = statusPedidos?.find(s => s.id === novoStatusId);
    const statusNome = statusSelecionado?.descricao || 'novo status';

    setConfirmarModal({
      isOpen: true,
      pedidoId,
      novoStatusId,
      statusNome,
    });
  };

  const confirmarAlteracaoStatus = () => {
    if (confirmarModal.pedidoId && confirmarModal.novoStatusId) {
      alterarStatus({
        pedidoId: confirmarModal.pedidoId,
        novoStatusId: confirmarModal.novoStatusId
      });
    }
    setConfirmarModal({
      isOpen: false,
      pedidoId: null,
      novoStatusId: null,
      statusNome: '',
    });
  };

  const cancelarAlteracaoStatus = () => {
    setConfirmarModal({
      isOpen: false,
      pedidoId: null,
      novoStatusId: null,
      statusNome: '',
    });
  };

  const handleConfirmarAdicionarItens = () => {
    if (!modalAdicionarItens.mesaId) return;
    const itens = Object.entries(modalAdicionarItens.quantidades).filter(([, q]) => q > 0);
    if (itens.length === 0) {
      toast.error('Selecione pelo menos um produto', {
        duration: 3000,
        position: 'top-center',
      });
      return;
    }
    const mesaId = modalAdicionarItens.mesaId;
    const saboresSnapshot = modalAdicionarItens.sabores;
    setModalAdicionarItens({ mesaId: null, aberto: false, quantidades: {}, sabores: {} });
    itens.forEach(([produtoId, quantidade]) => {
      const produtoIdNum = parseInt(produtoId);
      adicionarItem({ pedidoId: mesaId, request: { produtoId: produtoIdNum, quantidade, sabor: saboresSnapshot[produtoIdNum] || undefined } });
    });
  };

  const handleConfirmarFecharMesa = () => {
    if (!mesaParaFechar || !modalFecharMesa.tipoPagamentoId) {
      toast.error('Selecione o tipo de pagamento', {
        duration: 3000,
        position: 'top-center',
      });
      return;
    }
    fecharMesa(
      { pedidoId: mesaParaFechar.id, request: { tipoPagamentoId: modalFecharMesa.tipoPagamentoId } },
      { onSuccess: () => setModalFecharMesa({ mesaId: null, tipoPagamentoId: null, modo: 'unica', itensSelecionados: {} }) }
    );
  };

  const handlePagarSelecionados = () => {
    if (!mesaParaFechar || !modalFecharMesa.tipoPagamentoId) {
      toast.error('Selecione o tipo de pagamento', {
        duration: 3000,
        position: 'top-center',
      });
      return;
    }
    if (!temItensSelecionados) {
      toast.error('Selecione pelo menos um item', {
        duration: 3000,
        position: 'top-center',
      });
      return;
    }

    const itens = Object.entries(modalFecharMesa.itensSelecionados)
      .filter(([, qty]) => qty > 0)
      .map(([id, qty]) => ({ itemPedidoId: parseInt(id), quantidade: qty }));

    pagamentoParcial(
      { pedidoId: mesaParaFechar.id, request: { tipoPagamentoId: modalFecharMesa.tipoPagamentoId, itens } },
      {
        onSuccess: (data) => {
          if (data.mesaFechada) {
            setModalFecharMesa({ mesaId: null, tipoPagamentoId: null, modo: 'unica', itensSelecionados: {} });
          } else {
            setModalFecharMesa(prev => ({ ...prev, tipoPagamentoId: null, itensSelecionados: {} }));
          }
        },
      }
    );
  };

  const handleConfirmarEditarDesconto = () => {
    if (modalEditarDesconto.pedidoId === null) return;
    const descontoNum = parseFloat(modalEditarDesconto.descontoAtual.replace(',', '.')) || 0;
    atualizarDesconto(
      { pedidoId: modalEditarDesconto.pedidoId, desconto: descontoNum },
      { onSuccess: () => setModalEditarDesconto({ pedidoId: null, descontoAtual: '' }) }
    );
  };

  const handleConfirmarCancelarPedido = () => {
    if (modalCancelarPedido.pedidoId === null) return;
    cancelarPedido(modalCancelarPedido.pedidoId, {
      onSuccess: () => setModalCancelarPedido({ isOpen: false, pedidoId: null, numeroMesa: null }),
    });
  };

  const handleConfirmarRemoverItem = () => {
    if (modalRemoverItem.pedidoId === null || modalRemoverItem.itemId === null) return;
    removerItem(
      { pedidoId: modalRemoverItem.pedidoId, itemId: modalRemoverItem.itemId },
      { onSuccess: () => setModalRemoverItem({ isOpen: false, pedidoId: null, itemId: null, itemNome: '' }) }
    );
  };

  return (
    <div className="animate-fadeIn">
      <h1 className="text-3xl md:text-4xl font-display font-bold text-neutral-900 mb-8">PDV - Ponto de Venda</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl shadow-md border border-neutral-100 p-6">
          <div className="flex items-center gap-3 mb-4">
            <h2 className="text-xl font-display font-bold text-neutral-900">Produtos</h2>
            <div className="flex-1 relative">
              <input
                type="text"
                value={buscaProduto}
                onChange={(e) => setBuscaProduto(e.target.value)}
                placeholder="Buscar produto..."
                className="w-full pl-9 pr-8 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {buscaProduto && (
                <button
                  onClick={() => setBuscaProduto('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>
          {loadingProdutos ? (
            <p className="text-neutral-600 font-medium">Carregando produtos...</p>
          ) : produtosPorCategoria.length > 0 ? (
            <div className="space-y-5">
              {produtosPorCategoria.map(({ categoria, produtos: prods }) => (
                <div key={categoria}>
                  <h3 className="text-sm font-semibold text-neutral-500 uppercase tracking-wider mb-2 border-b border-neutral-100 pb-1">
                    {categoria}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {prods.map((produto) => (
                      <div
                        key={produto.id}
                        className={`border border-neutral-200 rounded-xl p-4 hover:shadow-lg hover:border-primary-300 transition-all duration-200 cursor-pointer hover:scale-[1.02] ${
                          produtoAnimando === produto.id ? 'animate-added-to-cart' : ''
                        }`}
                        onClick={() => handleClickProduto(produto)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-semibold text-neutral-900">{produto.nome}</h3>
                            {produto.descricao && (
                              <p className="text-sm text-neutral-600">{produto.descricao}</p>
                            )}
                          </div>
                          <p className="text-lg font-bold text-primary-600 ml-3 whitespace-nowrap">
                            {formatCurrency(produto.precoUnitario)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-neutral-500 text-center py-8">Nenhum produto disponível</p>
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Carrinho</h2>
          {carrinho.length > 0 ? (
            <div className="space-y-4">
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {carrinho.map((item) => (
                  <div key={item.id} className="flex items-center justify-between pb-3 border-b">
                    <div className="flex-1">
                      <p className="font-medium text-neutral-900">{item.produtoNome}</p>
                      {item.sabor && (
                        <p className="text-xs text-neutral-500 italic">• {item.sabor}</p>
                      )}
                      <p className="text-sm text-neutral-500">{formatCurrency(item.precoUnitario)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => atualizarQuantidade(item.id, item.quantidade - 1)}
                        className="w-8 h-8 flex items-center justify-center bg-gray-200 rounded hover:bg-gray-300"
                      >
                        -
                      </button>
                      <span className="w-8 text-center font-semibold">{item.quantidade}</span>
                      <button
                        onClick={() => atualizarQuantidade(item.id, item.quantidade + 1)}
                        className="w-8 h-8 flex items-center justify-center bg-gray-200 rounded hover:bg-gray-300"
                      >
                        +
                      </button>
                      <button
                        onClick={() => removerDoCarrinho(item.id)}
                        className="ml-2 text-red-600 hover:text-red-800"
                      >
                        X
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-2 pt-4 border-t">
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600">Subtotal:</span>
                  <span className="font-semibold">{formatCurrency(subtotal)}</span>
                </div>

                <div className="flex items-center gap-2">
                  <label className="text-sm text-neutral-600">Desconto:</label>
                  <input
                    type="text"
                    value={desconto}
                    onChange={(e) => setDesconto(e.target.value.replace(/[^0-9.,]/g, ''))}
                    className="flex-1 px-2 py-1 border rounded text-sm"
                    placeholder="0.00"
                  />
                </div>

                <div className="flex justify-between text-lg font-bold pt-2 border-t">
                  <span>Total:</span>
                  <span className="text-blue-600">{formatCurrency(total)}</span>
                </div>
              </div>

              {quantidadeMesas > 0 && (
                <div className="space-y-1 pt-2">
                  <label className="block text-sm font-medium text-gray-700">Mesa</label>
                  <select
                    value={numeroMesa}
                    onChange={(e) => setNumeroMesa(e.target.value === '' ? '' : parseInt(e.target.value))}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Sem mesa</option>
                    {Array.from({ length: quantidadeMesas }, (_, i) => i + 1).map((n) => (
                      <option key={n} value={n}>Mesa {n}</option>
                    ))}
                  </select>
                </div>
              )}

              {quantidadeMesas > 0 && (
                <div className="flex items-center gap-2 pt-2">
                  <input
                    type="checkbox"
                    id="pago"
                    checked={pago}
                    onChange={(e) => {
                      setPago(e.target.checked);
                      if (!e.target.checked) setTipoPagamentoId(null);
                    }}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="pago" className="text-sm font-medium text-gray-700 cursor-pointer">
                    Pedido pago
                  </label>
                </div>
              )}

              {(pago || quantidadeMesas < 1) && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Tipo de Pagamento
                  </label>
                  {loadingTipos ? (
                    <p className="text-sm text-neutral-500">Carregando...</p>
                  ) : (
                    <select
                      value={tipoPagamentoId || ''}
                      onChange={(e) => setTipoPagamentoId(parseInt(e.target.value))}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecione...</option>
                      {tiposPagamento?.map((tipo) => (
                        <option key={tipo.id} value={tipo.id}>
                          {tipo.descricao}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  id="retroativa"
                  checked={vendaRetroativa}
                  onChange={(e) => {
                    setVendaRetroativa(e.target.checked);
                    if (!e.target.checked) setDataHoraRetroativa('');
                  }}
                  className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-400"
                />
                <label htmlFor="retroativa" className="text-sm font-medium text-neutral-500 cursor-pointer">
                  Venda retroativa
                </label>
              </div>

              {vendaRetroativa && (
                <div className="mt-1">
                  <label className="block text-xs text-neutral-500 mb-1">Data e hora da venda</label>
                  <input
                    type="date"
                    value={dataHoraRetroativa}
                    max={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => setDataHoraRetroativa(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                </div>
              )}

              {isGerente && funcionarios && funcionarios.filter(f => f.ativo).length > 0 && (
                <div className="space-y-1 pt-1">
                  <label className="block text-sm font-medium text-gray-700">Funcionário responsável</label>
                  <select
                    value={funcionarioCarrinhoId ?? ''}
                    onChange={(e) => setFuncionarioCarrinhoId(e.target.value ? parseInt(e.target.value) : null)}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Sem funcionário</option>
                    {funcionarios.filter(f => f.ativo).map(f => (
                      <option key={f.id} value={f.id}>{f.nome}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="space-y-2">
                <Button
                  onClick={finalizarVenda}
                  disabled={isPending}
                  variant="primary"
                  size="lg"
                  fullWidth
                >
                  {isPending ? 'Finalizando...' : quantidadeMesas < 1 ? 'Finalizar Venda' : pago ? 'Finalizar Venda' : 'Abrir Mesa'}
                </Button>
                <Button
                  onClick={() => {
                    setCarrinho([]);
                    setDesconto('');
                    setTipoPagamentoId(null);
                    setNumeroMesa('');
                    setFuncionarioCarrinhoId(null);
                  }}
                  variant="secondary"
                  size="md"
                  fullWidth
                >
                  Limpar Carrinho
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-neutral-500 text-center py-8">Carrinho vazio</p>
          )}
        </div>
      </div>

      {/* Mesas Abertas */}
      {quantidadeMesas > 0 && (
      <div className="mt-8 bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-neutral-900 mb-4">Mesas Abertas</h2>

        {loadingMesas ? (
          <p className="text-neutral-500 text-center py-4">Carregando mesas...</p>
        ) : mesasAbertas && mesasAbertas.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {mesasAbertas.map((mesa) => (
              <div key={mesa.id} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-neutral-900">
                      {mesa.numeroMesa ? `Mesa ${mesa.numeroMesa}` : 'Sem mesa'} &nbsp;
                      <span className="text-xs text-neutral-500 font-normal">#{mesa.id}</span>
                    </h3>
                    <span className="text-xs text-neutral-500">{formatDateTime(mesa.dataHora)}</span>
                  </div>
                  <button
                    onClick={() => setModalCancelarPedido({ isOpen: true, pedidoId: mesa.id, numeroMesa: mesa.numeroMesa ?? null })}
                    className="bg-red-500 hover:bg-red-600 text-white font-bold rounded w-7 h-7 flex items-center justify-center transition-colors shadow-sm hover:shadow-md"
                    title="Cancelar pedido"
                  >
                    ×
                  </button>
                </div>

                <div className="space-y-1">
                  {agruparItens(mesa.itens).map((item) => (
                    <div key={`${item.produtoId}__${item.sabor || ''}`} className="flex items-center justify-between text-sm">
                      <div>
                        <span className="text-gray-700">{item.quantidade}x {item.nomeProduto}</span>
                        {item.sabor && <p className="text-xs text-neutral-400 italic">{item.sabor}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-neutral-600">{formatCurrency(item.valorTotal)}</span>
                        <button
                          onClick={() => setModalRemoverItem({
                            isOpen: true,
                            pedidoId: mesa.id,
                            itemId: item.id,
                            itemNome: item.nomeProduto
                          })}
                          className="text-red-500 hover:text-red-700 text-xs font-bold"
                          title="Remover item"
                        >
                          x
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t pt-2 space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-600">Subtotal:</span>
                    <span>{formatCurrency(mesa.valorSubtotal)}</span>
                  </div>
                  {mesa.desconto > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-neutral-600">Desconto:</span>
                      <span className="text-red-600">-{formatCurrency(mesa.desconto)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold text-lg">
                    <span>Total:</span>
                    <span className="text-blue-600">{formatCurrency(mesa.valorTotal)}</span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => setModalAdicionarItens({ mesaId: mesa.id, aberto: true, quantidades: {}, sabores: {} })}
                    variant="secondary"
                    size="sm"
                    fullWidth
                  >
                    + Adicionar itens
                  </Button>
                  <Button
                    onClick={() => setModalEditarDesconto({ pedidoId: mesa.id, descontoAtual: String(mesa.desconto || '') })}
                    variant="secondary"
                    size="sm"
                    fullWidth
                  >
                    Editar desconto
                  </Button>
                  <Button
                    onClick={() => setModalFecharMesa({ mesaId: mesa.id, tipoPagamentoId: null, modo: 'unica', itensSelecionados: {} })}
                    variant="primary"
                    size="sm"
                    fullWidth
                  >
                    Fechar mesa
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-neutral-500 text-center py-4">Nenhuma mesa aberta no momento</p>
        )}
      </div>
      )}

      {/* Gestão de Pedidos do Dia */}
      <div className="mt-8 bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-neutral-900 mb-4">Gestão de Pedidos do Dia</h2>

        {loadingPedidos || loadingStatus ? (
          <p className="text-neutral-500 text-center py-8">Carregando pedidos...</p>
        ) : dadosPedidos && dadosPedidos.pedidos && dadosPedidos.pedidos.length > 0 ? (
          <div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Data/Hora
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Mesa
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Funcionário
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Valor
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Alterar Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Detalhes
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {dadosPedidos.pedidos.map((pedido) => (
                    <React.Fragment key={pedido.id}>
                      <tr className="hover:bg-neutral-50">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">#{pedido.id}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{formatDateTime(pedido.dataHora)}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {pedido.numeroMesa ? `Mesa ${pedido.numeroMesa}` : '-'}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {pedido.nomeFuncionario ?? '-'}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm font-semibold text-gray-900">
                            {formatCurrency(pedido.valorTotal)}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(
                              pedido.statusDescricao
                            )}`}
                          >
                            {pedido.statusDescricao}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <select
                            value={pedido.statusPedidoId}
                            onChange={(e) =>
                              handleAlterarStatus(pedido.id, parseInt(e.target.value))
                            }
                            disabled={alterandoStatus}
                            className="px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-neutral-100 disabled:cursor-not-allowed"
                          >
                            {statusPedidos?.map((status) => (
                              <option key={status.id} value={status.id}>
                                {status.descricao}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <button
                            onClick={() =>
                              setPedidoExpandido(pedidoExpandido === pedido.id ? null : pedido.id)
                            }
                            className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                          >
                            {pedidoExpandido === pedido.id ? 'Ocultar' : 'Ver'}
                          </button>
                        </td>
                      </tr>
                      {pedidoExpandido === pedido.id && (
                        <tr>
                          <td colSpan={8} className="px-4 py-4 bg-neutral-50">
                            <div className="space-y-3">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <p className="text-sm font-medium text-gray-700">Subtotal:</p>
                                  <p className="text-sm text-gray-900">
                                    {formatCurrency(pedido.valorSubtotal)}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-700">Desconto:</p>
                                  <p className="text-sm text-gray-900">
                                    {formatCurrency(pedido.desconto)}
                                  </p>
                                </div>
                                {pedido.valorEstornado > 0 && (
                                  <div>
                                    <p className="text-sm font-medium text-gray-700">Estornado:</p>
                                    <p className="text-sm text-red-600">
                                      {formatCurrency(pedido.valorEstornado)}
                                    </p>
                                  </div>
                                )}
                                {isGerente && (
                                  <div className="col-span-2">
                                    <p className="text-sm font-medium text-gray-700 mb-1">Funcionário responsável</p>
                                    {editandoFuncionario?.pedidoId === pedido.id ? (
                                      <div className="flex items-center gap-2">
                                        <select
                                          value={editandoFuncionario.usuarioId ?? ''}
                                          onChange={(e) => setEditandoFuncionario({
                                            pedidoId: pedido.id,
                                            usuarioId: e.target.value ? parseInt(e.target.value) : null,
                                          })}
                                          className="flex-1 px-2 py-1 border border-neutral-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        >
                                          <option value="">Sem funcionário</option>
                                          {funcionarios?.map(f => (
                                            <option key={f.id} value={f.id}>{f.nome}</option>
                                          ))}
                                        </select>
                                        <Button
                                          size="sm"
                                          variant="primary"
                                          disabled={alterandoFuncionario}
                                          onClick={() => {
                                            alterarFuncionario({ pedidoId: pedido.id, usuarioId: editandoFuncionario.usuarioId });
                                            setEditandoFuncionario(null);
                                          }}
                                        >
                                          Salvar
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="secondary"
                                          onClick={() => setEditandoFuncionario(null)}
                                        >
                                          Cancelar
                                        </Button>
                                      </div>
                                    ) : (
                                      <div className="flex items-center gap-2">
                                        <p className="text-sm text-gray-900">
                                          {pedido.nomeFuncionario ?? 'Sem funcionário'}
                                        </p>
                                        <button
                                          onClick={() => setEditandoFuncionario({
                                            pedidoId: pedido.id,
                                            usuarioId: pedido.usuarioId ?? null,
                                          })}
                                          className="text-xs text-primary-600 hover:text-primary-800 font-medium underline underline-offset-2"
                                        >
                                          Alterar
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>

                              <div>
                                <p className="text-sm font-medium text-gray-700 mb-2">Itens:</p>
                                <div className="bg-white rounded border overflow-x-auto">
                                  <table className="min-w-full">
                                    <thead className="bg-neutral-100">
                                      <tr>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-neutral-600">
                                          Produto
                                        </th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-neutral-600">
                                          Sabor
                                        </th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-neutral-600">
                                          Qtd
                                        </th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-neutral-600">
                                          Preço Unit.
                                        </th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-neutral-600">
                                          Total
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                      {agruparItens(pedido.itens ?? []).map((item) => (
                                        <tr key={`${item.produtoId}__${item.sabor || ''}`}>
                                          <td className="px-3 py-2 text-sm text-gray-900">
                                            {item.nomeProduto}
                                          </td>
                                          <td className="px-3 py-2 text-sm text-neutral-500 italic">
                                            {item.sabor || '—'}
                                          </td>
                                          <td className="px-3 py-2 text-sm text-gray-900">
                                            {item.quantidade}
                                          </td>
                                          <td className="px-3 py-2 text-sm text-gray-900">
                                            {formatCurrency(item.precoUnitario)}
                                          </td>
                                          <td className="px-3 py-2 text-sm font-semibold text-gray-900">
                                            {formatCurrency(item.valorTotal)}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex items-center justify-between border-t pt-4">
              <div className="text-sm text-gray-700">
                Mostrando {(paginaAtual - 1) * tamanhoPagina + 1} a{' '}
                {Math.min(paginaAtual * tamanhoPagina, dadosPedidos.totalCount)} de{' '}
                {dadosPedidos.totalCount} pedidos
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => setPaginaAtual((prev) => Math.max(1, prev - 1))}
                  disabled={paginaAtual === 1}
                  variant="secondary"
                  size="sm"
                >
                  Anterior
                </Button>
                <div className="flex items-center px-3 py-1 text-sm text-gray-700">
                  Página {paginaAtual} de {dadosPedidos.totalPaginas}
                </div>
                <Button
                  onClick={() => setPaginaAtual((prev) => Math.min(dadosPedidos.totalPaginas, prev + 1))}
                  disabled={paginaAtual >= dadosPedidos.totalPaginas}
                  variant="secondary"
                  size="sm"
                >
                  Próxima
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-neutral-500 text-center py-8">Nenhum pedido encontrado hoje</p>
        )}
      </div>

      {/* Modal Adicionar Itens */}
      {modalAdicionarItens.aberto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl p-4 md:p-6 w-full max-w-lg max-h-[90vh] flex flex-col">
            <h2 className="text-xl font-bold text-neutral-900 mb-4">Adicionar Itens</h2>
            <div className="flex-1 overflow-y-auto space-y-2">
              {produtos?.filter((produto) => !produto.sobEncomenda).sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')).map((produto) => (
                <div key={produto.id} className="p-3 border rounded-lg space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-neutral-900">{produto.nome}</p>
                      <p className="text-sm text-neutral-500">{formatCurrency(produto.precoUnitario)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setModalAdicionarItens(prev => ({
                          ...prev,
                          quantidades: {
                            ...prev.quantidades,
                            [produto.id]: Math.max(0, (prev.quantidades[produto.id] || 0) - 1)
                          }
                        }))}
                        className="w-8 h-8 flex items-center justify-center bg-gray-200 rounded hover:bg-gray-300"
                      >
                        -
                      </button>
                      <span className="w-8 text-center font-semibold">
                        {modalAdicionarItens.quantidades[produto.id] || 0}
                      </span>
                      <button
                        onClick={() => setModalAdicionarItens(prev => ({
                          ...prev,
                          quantidades: {
                            ...prev.quantidades,
                            [produto.id]: (prev.quantidades[produto.id] || 0) + 1
                          }
                        }))}
                        className="w-8 h-8 flex items-center justify-center bg-gray-200 rounded hover:bg-gray-300"
                      >
                        +
                      </button>
                    </div>
                  </div>
                  {modalAdicionarItens.quantidades[produto.id] > 0 && produto.sabores && produto.sabores.length > 0 && (
                    <select
                      value={modalAdicionarItens.sabores[produto.id] || ''}
                      onChange={(e) => setModalAdicionarItens(prev => ({
                        ...prev,
                        sabores: { ...prev.sabores, [produto.id]: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">Sem sabor</option>
                      {produto.sabores.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  )}
                </div>
              ))}
            </div>
            <div className="flex gap-3 mt-4 pt-4 border-t">
              <Button onClick={handleConfirmarAdicionarItens} variant="primary" size="md" fullWidth disabled={adicionandoItem}>
                {adicionandoItem ? 'Adicionando...' : 'Adicionar'}
              </Button>
              <Button
                onClick={() => setModalAdicionarItens({ mesaId: null, aberto: false, quantidades: {}, sabores: {} })}
                variant="secondary"
                size="md"
                fullWidth
                disabled={adicionandoItem}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Fechar Mesa */}
      {mesaParaFechar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg max-h-[90vh] flex flex-col">
            <h2 className="text-xl font-bold text-neutral-900 mb-4">
              Fechar {mesaParaFechar.numeroMesa ? `Mesa ${mesaParaFechar.numeroMesa}` : 'Pedido'}
            </h2>

            {/* Toggle modo */}
            <div className="flex rounded-lg border border-neutral-200 mb-4">
              <button
                onClick={() => setModalFecharMesa(prev => ({ ...prev, modo: 'unica', itensSelecionados: {} }))}
                className={`flex-1 py-2 px-4 text-sm font-medium rounded-l-lg transition-colors ${
                  modalFecharMesa.modo === 'unica'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-neutral-600 hover:bg-neutral-50'
                }`}
              >
                Conta única
              </button>
              <button
                onClick={() => setModalFecharMesa(prev => ({ ...prev, modo: 'dividir', itensSelecionados: {} }))}
                className={`flex-1 py-2 px-4 text-sm font-medium rounded-r-lg transition-colors ${
                  modalFecharMesa.modo === 'dividir'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-neutral-600 hover:bg-neutral-50'
                }`}
              >
                Dividir conta
              </button>
            </div>

            {/* Modo Conta Única */}
            {modalFecharMesa.modo === 'unica' && (
              <>
                <div className="space-y-2 mb-4">
                  {agruparItens(mesaParaFechar.itens).map((item) => (
                    <div key={`${item.produtoId}__${item.sabor || ''}`} className="flex justify-between text-sm">
                      <div>
                        <span>{item.quantidade}x {item.nomeProduto}</span>
                        {item.sabor && <p className="text-xs text-neutral-400 italic">{item.sabor}</p>}
                      </div>
                      <span className="font-medium">{formatCurrency(item.valorTotal)}</span>
                    </div>
                  ))}
                </div>

                <div className="border-t pt-3 space-y-1 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-600">Subtotal:</span>
                    <span>{formatCurrency(mesaParaFechar.valorSubtotal)}</span>
                  </div>
                  {mesaParaFechar.desconto > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-neutral-600">Desconto:</span>
                      <span className="text-red-600">-{formatCurrency(mesaParaFechar.desconto)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg">
                    <span>Total:</span>
                    <span className="text-blue-600">{formatCurrency(mesaParaFechar.valorTotal)}</span>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Pagamento</label>
                  {loadingTipos ? (
                    <p className="text-sm text-neutral-500">Carregando...</p>
                  ) : (
                    <select
                      value={modalFecharMesa.tipoPagamentoId || ''}
                      onChange={(e) => setModalFecharMesa(prev => ({ ...prev, tipoPagamentoId: parseInt(e.target.value) }))}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecione...</option>
                      {tiposPagamento?.map((tipo) => (
                        <option key={tipo.id} value={tipo.id}>{tipo.descricao}</option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={handleConfirmarFecharMesa}
                    variant="primary"
                    size="md"
                    fullWidth
                    disabled={fechandoMesa || !modalFecharMesa.tipoPagamentoId}
                  >
                    {fechandoMesa ? 'Fechando...' : 'Confirmar'}
                  </Button>
                  <Button
                    onClick={() => setModalFecharMesa({ mesaId: null, tipoPagamentoId: null, modo: 'unica', itensSelecionados: {} })}
                    variant="secondary"
                    size="md"
                    fullWidth
                    disabled={fechandoMesa}
                  >
                    Cancelar
                  </Button>
                </div>
              </>
            )}

            {/* Modo Dividir Conta */}
            {modalFecharMesa.modo === 'dividir' && (
              <>
                <div className="flex-1 overflow-y-auto space-y-2 mb-4">
                  {mesaParaFechar.itens.map((item) => {
                    const qtdSelecionada = modalFecharMesa.itensSelecionados[item.id] || 0;
                    const selecionado = qtdSelecionada > 0;

                    return (
                      <div
                        key={item.id}
                        className={`flex items-center gap-3 p-3 border rounded-lg transition-colors ${
                          selecionado ? 'border-primary-300 bg-primary-50' : 'border-neutral-200'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selecionado}
                          onChange={(e) => {
                            setModalFecharMesa(prev => ({
                              ...prev,
                              itensSelecionados: {
                                ...prev.itensSelecionados,
                                [item.id]: e.target.checked ? item.quantidade : 0,
                              },
                            }));
                          }}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-neutral-900">{item.nomeProduto}</p>
                          {item.sabor && <p className="text-xs text-neutral-400 italic">{item.sabor}</p>}
                          <p className="text-xs text-neutral-500">{formatCurrency(item.precoUnitario)} un.</p>
                        </div>
                        {selecionado && item.quantidade > 1 && (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => setModalFecharMesa(prev => ({
                                ...prev,
                                itensSelecionados: {
                                  ...prev.itensSelecionados,
                                  [item.id]: Math.max(1, qtdSelecionada - 1),
                                },
                              }))}
                              className="w-7 h-7 flex items-center justify-center bg-gray-200 rounded text-sm hover:bg-gray-300"
                            >
                              -
                            </button>
                            <span className="w-6 text-center text-sm font-semibold">{qtdSelecionada}</span>
                            <button
                              onClick={() => setModalFecharMesa(prev => ({
                                ...prev,
                                itensSelecionados: {
                                  ...prev.itensSelecionados,
                                  [item.id]: Math.min(item.quantidade, qtdSelecionada + 1),
                                },
                              }))}
                              className="w-7 h-7 flex items-center justify-center bg-gray-200 rounded text-sm hover:bg-gray-300"
                            >
                              +
                            </button>
                            <span className="text-xs text-neutral-400 ml-1">/{item.quantidade}</span>
                          </div>
                        )}
                        {(!selecionado || item.quantidade === 1) && (
                          <span className="text-sm text-neutral-500">x{item.quantidade}</span>
                        )}
                        <span className="text-sm font-medium min-w-[70px] text-right">
                          {formatCurrency(selecionado ? item.precoUnitario * qtdSelecionada : item.valorTotal)}
                        </span>
                      </div>
                    );
                  })}
                </div>

                <div className="border-t pt-3 space-y-1 mb-4">
                  {temItensSelecionados && (
                    <div className="flex justify-between text-sm font-semibold text-primary-700">
                      <span>Subtotal selecionados:</span>
                      <span>{formatCurrency(subtotalSelecionados)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm text-neutral-500">
                    <span>Total da mesa:</span>
                    <span>{formatCurrency(mesaParaFechar.valorTotal)}</span>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Pagamento</label>
                  {loadingTipos ? (
                    <p className="text-sm text-neutral-500">Carregando...</p>
                  ) : (
                    <select
                      value={modalFecharMesa.tipoPagamentoId || ''}
                      onChange={(e) => setModalFecharMesa(prev => ({ ...prev, tipoPagamentoId: parseInt(e.target.value) }))}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecione...</option>
                      {tiposPagamento?.map((tipo) => (
                        <option key={tipo.id} value={tipo.id}>{tipo.descricao}</option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={handlePagarSelecionados}
                    variant="primary"
                    size="md"
                    fullWidth
                    disabled={pagandoParcial || !modalFecharMesa.tipoPagamentoId || !temItensSelecionados}
                  >
                    {pagandoParcial ? 'Pagando...' : `Pagar selecionados`}
                  </Button>
                  <Button
                    onClick={() => setModalFecharMesa({ mesaId: null, tipoPagamentoId: null, modo: 'unica', itensSelecionados: {} })}
                    variant="secondary"
                    size="md"
                    fullWidth
                    disabled={pagandoParcial}
                  >
                    Cancelar
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal Editar Desconto */}
      {modalEditarDesconto.pedidoId !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-neutral-900 mb-4">Editar Desconto</h2>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Desconto (R$)</label>
              <input
                type="text"
                value={modalEditarDesconto.descontoAtual}
                onChange={(e) => setModalEditarDesconto(prev => ({
                  ...prev,
                  descontoAtual: e.target.value.replace(/[^0-9.,]/g, '')
                }))}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="0.00"
              />
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleConfirmarEditarDesconto}
                variant="primary"
                size="md"
                fullWidth
                disabled={atualizandoDesconto}
              >
                {atualizandoDesconto ? 'Atualizando...' : 'Confirmar'}
              </Button>
              <Button
                onClick={() => setModalEditarDesconto({ pedidoId: null, descontoAtual: '' })}
                variant="secondary"
                size="md"
                fullWidth
                disabled={atualizandoDesconto}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Cancelar Pedido */}
      <ConfirmModal
        isOpen={modalCancelarPedido.isOpen}
        title="Cancelar Pedido"
        message={`Tem certeza que deseja cancelar ${modalCancelarPedido.numeroMesa ? `a Mesa ${modalCancelarPedido.numeroMesa}` : 'este pedido'}? Esta ação não pode ser desfeita.`}
        confirmText="Sim, cancelar"
        cancelText="Não"
        onConfirm={handleConfirmarCancelarPedido}
        onCancel={() => setModalCancelarPedido({ isOpen: false, pedidoId: null, numeroMesa: null })}
      />

      {/* Modal Remover Item */}
      <ConfirmModal
        isOpen={modalRemoverItem.isOpen}
        title="Remover Item"
        message={`Tem certeza que deseja remover "${modalRemoverItem.itemNome}" do pedido?`}
        confirmText="Sim, remover"
        cancelText="Cancelar"
        onConfirm={handleConfirmarRemoverItem}
        onCancel={() => setModalRemoverItem({ isOpen: false, pedidoId: null, itemId: null, itemNome: '' })}
      />

      <ConfirmModal
        isOpen={confirmarModal.isOpen}
        title="Confirmar Alteração de Status"
        message={`Deseja alterar o status do pedido #${confirmarModal.pedidoId} para "${confirmarModal.statusNome}"?`}
        confirmText="Confirmar"
        cancelText="Cancelar"
        onConfirm={confirmarAlteracaoStatus}
        onCancel={cancelarAlteracaoStatus}
      />

      {/* Modal Seleção de Sabor */}
      {modalSabor.isOpen && modalSabor.produto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm">
            <h2 className="text-xl font-bold text-neutral-900 mb-1">{modalSabor.produto.nome}</h2>
            <p className="text-sm text-neutral-500 mb-4">Selecione o sabor:</p>
            <div className="space-y-2">
              {modalSabor.produto.sabores.map((sabor) => (
                <button
                  key={sabor}
                  onClick={() => {
                    adicionarAoCarrinho(modalSabor.produto!.id, modalSabor.produto!.nome, modalSabor.produto!.preco, sabor);
                    setModalSabor({ isOpen: false, produto: null });
                  }}
                  className="w-full py-3 px-4 text-left font-medium border border-neutral-200 rounded-lg hover:bg-primary-50 hover:border-primary-300 transition-colors"
                >
                  {sabor}
                </button>
              ))}
              <button
                onClick={() => {
                  adicionarAoCarrinho(modalSabor.produto!.id, modalSabor.produto!.nome, modalSabor.produto!.preco, undefined);
                  setModalSabor({ isOpen: false, produto: null });
                }}
                className="w-full py-3 px-4 text-left text-neutral-500 border border-dashed border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors"
              >
                Sem sabor definido
              </button>
            </div>
            <button
              onClick={() => setModalSabor({ isOpen: false, produto: null })}
              className="mt-4 w-full py-2 text-sm text-neutral-400 hover:text-neutral-600"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PDV;
